import * as React from 'react';
import Box from '@mui/material/Box';

export interface StackBuilderLayoutProps {
  header: React.ReactNode;
  library: React.ReactNode;
  stack: React.ReactNode;
  config: React.ReactNode;
  /** Above the columns, full width: a save error, a warning. */
  banner?: React.ReactNode;
}

/**
 * Full-height workbench: header, then three columns in a 3 / 4 / 5 split of twelve — the offer
 * library, the stack being built, and its configuration, which gets the most room because the
 * criteria panel and the per-type defaults need it. Each column scrolls on its own. Below the md
 * breakpoint the columns stack and the page scrolls instead.
 */
export default function StackBuilderLayout({ header, library, stack, config, banner }: StackBuilderLayoutProps) {
  return (
    <Box
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', minHeight: 0 }}
    >
      {header}
      {banner}
      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'grid',
          gap: 1.5,
          p: 1.5,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 3fr) minmax(0, 4fr) minmax(0, 5fr)' },
          gridAutoRows: { xs: 'minmax(480px, auto)', md: 'auto' },
          overflowY: { xs: 'auto', md: 'hidden' },
        }}
      >
        {library}
        {stack}
        {config}
      </Box>
    </Box>
  );
}

/**
 * A column: a paper panel whose body scrolls under a fixed heading. `emphasis` marks the
 * column the page is about — the stack — with a primary heading band and frame, so it reads as
 * the work surface and the library beside it as a source to drag from. The body keeps the same
 * paper as the other columns: a tint there fought the scrollbar and the inactive-offer rows.
 */
export function BuilderPanel({
  title,
  aside,
  toolbar,
  children,
  footer,
  label,
  bodyRef,
  bodyProps,
  emphasis = false,
}: {
  title?: React.ReactNode;
  aside?: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  label: string;
  bodyRef?: React.Ref<HTMLDivElement>;
  bodyProps?: React.HTMLAttributes<HTMLDivElement>;
  emphasis?: boolean;
}) {
  return (
    <Box
      component='section'
      aria-label={label}
      sx={(theme) => {
        const primary = (theme.vars ?? theme).palette.primary.main;
        return {
          bgcolor: 'background.paper',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          ...(emphasis && {
            outline: `2px solid ${primary}`,
            outlineOffset: -2,
            boxShadow: `0 6px 20px ${theme.alpha(primary, 0.25)}`,
          }),
        };
      }}
    >
      {(title || aside) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            px: 1.5,
            pt: 1.25,
            pb: 1,
            // The emphasised column's heading is a solid primary band: the strongest cue on the page.
            ...(emphasis && { bgcolor: 'primary.main', color: 'primary.contrastText', mb: 1 }),
          }}
        >
          {title && (
            <Box component='h2' sx={{ m: 0, typography: 'subtitle1', fontWeight: 600 }}>
              {title}
            </Box>
          )}
          {aside && (
            <Box
              sx={{
                color: emphasis ? 'inherit' : 'text.secondary',
                opacity: emphasis ? 0.85 : 1,
                typography: 'caption',
              }}
            >
              {aside}
            </Box>
          )}
        </Box>
      )}
      {toolbar && <Box sx={{ px: 1.5, pb: 1 }}>{toolbar}</Box>}
      <Box ref={bodyRef} {...bodyProps} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: 1.5, pb: 1.5 }}>
        {children}
      </Box>
      {footer}
    </Box>
  );
}
